import { useState, type FormEvent, type KeyboardEvent } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  KeyRound,
  Loader2,
  Mail,
  MapPin,
  ShieldCheck,
} from 'lucide-react'
import { Link } from 'react-router'
import { EVENT } from '../config/event'
import '../styles/login-concepts.css'

type LoginConceptVariant = 'editorial' | 'midnight' | 'coral' | 'grid' | 'garden'
type Stage = 'email' | 'code' | 'password' | 'success'

interface ConceptSpec {
  number: string
  name: string
  shortName: string
  kicker: string
  headline: string
  accentWord: string
  description: string
  formTitle: string
  formSubtitle: string
  sideNote: string
}

const CONCEPTS: Record<LoginConceptVariant, ConceptSpec> = {
  editorial: {
    number: '01',
    name: 'Editorial',
    shortName: 'The programme issue',
    kicker: 'A UiTM professional learning gathering',
    headline: 'Make room for better practice.',
    accentWord: 'better practice',
    description: 'A considered entrance for a three-day programme of shared knowledge, clinical craft and useful conversations.',
    formTitle: 'Enter the programme',
    formSubtitle: 'Use the email address on your registration.',
    sideNote: 'Quiet hierarchy · warm paper · publication-style type',
  },
  midnight: {
    number: '02',
    name: 'Midnight Signal',
    shortName: 'After-hours access',
    kicker: 'CPD / 2026 // secure participant channel',
    headline: 'Ready when the room lights dim.',
    accentWord: 'room lights dim',
    description: 'A high-contrast control-room treatment for fast sign-in at the registration desk or in a dim venue.',
    formTitle: 'Identify yourself',
    formSubtitle: 'Your email chooses the next secure step.',
    sideNote: 'Dark surface · signal green · terminal-inspired type',
  },
  coral: {
    number: '03',
    name: 'Coral Field',
    shortName: 'People first',
    kicker: 'Universiti Teknologi MARA · Pusat Kesihatan',
    headline: 'Come for the sessions. Leave with a clearer next step.',
    accentWord: 'clearer next step',
    description: 'A warm, human entry point that makes the purpose of the event visible before asking for an address.',
    formTitle: 'Welcome to CPD 2026',
    formSubtitle: 'Let’s get you to your programme.',
    sideNote: 'Coral energy · deep teal · confident rounded display',
  },
  grid: {
    number: '04',
    name: 'Civic Grid',
    shortName: 'The information desk',
    kicker: 'EVENT 06 / PARTICIPANT ACCESS',
    headline: 'Everything you need, in the right order.',
    accentWord: 'right order',
    description: 'A structured civic interface built around wayfinding, clear labels and an obvious first action.',
    formTitle: 'Participant sign-in',
    formSubtitle: 'One address. One secure next step.',
    sideNote: 'Blueprint blue · signal amber · Swiss-style layout',
  },
  garden: {
    number: '05',
    name: 'Quiet Garden',
    shortName: 'A softer welcome',
    kicker: 'CONTINUING PROFESSIONAL DEVELOPMENT',
    headline: 'Take a breath. Your programme is waiting.',
    accentWord: 'programme is waiting',
    description: 'A calmer, generous treatment for people arriving between sessions, on a phone, with only a minute to spare.',
    formTitle: 'Continue to your CPD',
    formSubtitle: 'We’ll use your email to keep the next step simple.',
    sideNote: 'Sage canvas · ink green · alternating serif rhythm',
  },
}

const CONCEPT_ROUTES: Array<{ variant: LoginConceptVariant; path: string }> = [
  { variant: 'editorial', path: '/preview/login/editorial' },
  { variant: 'midnight', path: '/preview/login/midnight' },
  { variant: 'coral', path: '/preview/login/coral' },
  { variant: 'grid', path: '/preview/login/grid' },
  { variant: 'garden', path: '/preview/login/garden' },
]

export function getLoginConceptFromPath(pathname: string): LoginConceptVariant | null {
  return CONCEPT_ROUTES.find((route) => route.path === pathname)?.variant ?? null
}

export function LoginConceptsRoute({ variant }: { variant: LoginConceptVariant }) {
  const spec = CONCEPTS[variant]
  const [stage, setStage] = useState<Stage>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState<string[]>(['', '', '', '', '', ''])
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  function submitEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!email.trim() || busy) return
    setBusy(true)
    setError('')
    window.setTimeout(() => {
      setBusy(false)
      setStage(email.toLowerCase().includes('staff') || email.toLowerCase().includes('admin') ? 'password' : 'code')
    }, 420)
  }

  function submitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (code.join('').length !== 6 || busy) return
    setBusy(true)
    setError('')
    window.setTimeout(() => {
      setBusy(false)
      setStage('success')
    }, 520)
  }

  function submitPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!password.trim() || busy) return
    setBusy(true)
    setError('')
    window.setTimeout(() => {
      setBusy(false)
      setStage('success')
    }, 520)
  }

  function restart() {
    setStage('email')
    setCode(['', '', '', '', '', ''])
    setPassword('')
    setError('')
  }

  function updateDigit(index: number, rawValue: string) {
    const digits = rawValue.replace(/\D/g, '').slice(0, 6)
    if (!digits) {
      const next = [...code]
      next[index] = ''
      setCode(next)
      return
    }

    const next = [...code]
    digits.split('').forEach((digit, offset) => {
      if (index + offset < next.length) next[index + offset] = digit
    })
    setCode(next)
    document.getElementById(`concept-otp-${Math.min(index + digits.length, 5)}`)?.focus()
  }

  function handleDigitKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`concept-otp-${index - 1}`)?.focus()
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      document.getElementById(`concept-otp-${index - 1}`)?.focus()
    }
    if (event.key === 'ArrowRight' && index < 5) {
      document.getElementById(`concept-otp-${index + 1}`)?.focus()
    }
  }

  const heading = stage === 'email' ? spec.formTitle : stage === 'code' ? 'Check your inbox' : stage === 'password' ? 'Staff access' : 'You’re signed in'
  const subtitle = stage === 'email' ? spec.formSubtitle : stage === 'code' ? 'Enter the six-digit code we sent you.' : stage === 'password' ? 'Enter the password for this venue account.' : 'Your conference dashboard is ready.'

  return (
    <main className={`lc lc--${variant}`}>
      <div className="lc__texture" aria-hidden="true" />

      <header className="lc__topbar">
        <Link className="lc__brand" to="/login" aria-label="CPD 2026 live sign-in">
          <span className="lc__brand-mark">CPD</span>
          <span className="lc__brand-year">2026</span>
        </Link>
        <div className="lc__preview-label">UI concept {spec.number} / 05</div>
        <Link className="lc__live-link" to="/login">Live sign-in <ArrowRight size={14} aria-hidden="true" /></Link>
      </header>

      <nav className="lc__switcher" aria-label="Login concepts">
        <span className="lc__switcher-label">Compare</span>
        {CONCEPT_ROUTES.map((route) => (
          <Link
            key={route.variant}
            className={route.variant === variant ? 'lc__switcher-link lc__switcher-link--active' : 'lc__switcher-link'}
            to={route.path}
          >
            {CONCEPTS[route.variant].number}
            <span>{CONCEPTS[route.variant].name}</span>
          </Link>
        ))}
      </nav>

      <div className="lc__layout">
        <section className="lc__story" aria-labelledby="concept-headline">
          <div className="lc__story-topline">
            <span className="lc__index">{spec.number}</span>
            <span>{spec.kicker}</span>
          </div>

          <div className="lc__story-copy">
            <p className="lc__event-mark"><span>UiTM</span> / {spec.shortName}</p>
            <h1 id="concept-headline" className="lc__headline">
              {spec.headline.split(spec.accentWord)[0]}
              <em>{spec.accentWord}</em>
              {spec.headline.split(spec.accentWord)[1]}
            </h1>
            <p className="lc__description">{spec.description}</p>
          </div>

          <dl className="lc__event-meta">
            <div>
              <dt><CalendarDays size={15} aria-hidden="true" /> Dates</dt>
              <dd>{EVENT.dateLabel}</dd>
            </div>
            <div>
              <dt><MapPin size={15} aria-hidden="true" /> Venue</dt>
              <dd>{EVENT.venue}</dd>
            </div>
          </dl>

          <p className="lc__design-note">{spec.sideNote}</p>
        </section>

        <section className="lc__panel" aria-label="Preview sign in">
          <div className="lc__panel-heading" aria-live="polite">
            <p className="lc__panel-kicker">{stage === 'success' ? 'ACCESS GRANTED' : 'PARTICIPANT ACCESS'}</p>
            <h2>{heading}</h2>
            <p>{subtitle}</p>
          </div>

          {stage === 'email' && (
            <form className="lc__form" onSubmit={submitEmail}>
              <label className="lc__field-label" htmlFor="concept-email">Email address</label>
              <div className="lc__input-wrap">
                <Mail size={18} aria-hidden="true" />
                <input
                  id="concept-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="name@uitm.edu.my"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
              {error ? <p className="lc__error" role="alert">{error}</p> : null}
              <p className="lc__hint">We’ll identify whether this address uses a one-time code or staff password.</p>
              <button className="lc__primary" type="submit" disabled={busy || !email.trim()}>
                {busy ? <Loader2 className="lc__spin" size={18} aria-label="Checking" /> : <>Continue <ArrowRight size={17} aria-hidden="true" /></>}
              </button>
            </form>
          )}

          {stage === 'code' && (
            <form className="lc__form" onSubmit={submitCode}>
              <div className="lc__addressed">
                <span>Code sent to</span>
                <strong>{email.trim()}</strong>
                <button type="button" onClick={restart}>Change</button>
              </div>
              <fieldset className="lc__otp-fieldset">
                <legend>Six-digit verification code</legend>
                <div className="lc__otp-row">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      id={`concept-otp-${index}`}
                      className="lc__otp"
                      aria-label={`Digit ${index + 1} of 6`}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={digit}
                      onChange={(event) => updateDigit(index, event.target.value)}
                      onKeyDown={(event) => handleDigitKeyDown(index, event)}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
              </fieldset>
              {error ? <p className="lc__error" role="alert">{error}</p> : null}
              <p className="lc__hint">For this visual preview, any six digits complete the sign-in.</p>
              <button className="lc__primary" type="submit" disabled={busy || code.join('').length !== 6}>
                {busy ? <Loader2 className="lc__spin" size={18} aria-label="Verifying" /> : <>Verify and continue <ArrowRight size={17} aria-hidden="true" /></>}
              </button>
              <button className="lc__back" type="button" onClick={restart}><ArrowLeft size={14} aria-hidden="true" /> Use a different address</button>
            </form>
          )}

          {stage === 'password' && (
            <form className="lc__form" onSubmit={submitPassword}>
              <div className="lc__addressed">
                <span>Staff account</span>
                <strong>{email.trim()}</strong>
                <button type="button" onClick={restart}>Change</button>
              </div>
              <label className="lc__field-label" htmlFor="concept-password">Password</label>
              <div className="lc__input-wrap">
                <KeyRound size={18} aria-hidden="true" />
                <input
                  id="concept-password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Enter staff password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoFocus
                  required
                />
              </div>
              {error ? <p className="lc__error" role="alert">{error}</p> : null}
              <button className="lc__primary" type="submit" disabled={busy || !password.trim()}>
                {busy ? <Loader2 className="lc__spin" size={18} aria-label="Signing in" /> : <>Sign in <ArrowRight size={17} aria-hidden="true" /></>}
              </button>
              <button className="lc__back" type="button" onClick={restart}><ArrowLeft size={14} aria-hidden="true" /> Use a different address</button>
            </form>
          )}

          {stage === 'success' && (
            <div className="lc__success">
              <CheckCircle2 size={46} aria-hidden="true" />
              <p className="lc__success-title">Signed in successfully</p>
              <p>Your programme, attendance and certificate status are ready.</p>
              <button className="lc__primary" type="button" onClick={restart}>Preview again <ArrowRight size={17} aria-hidden="true" /></button>
            </div>
          )}

          <footer className="lc__panel-footer">
            <ShieldCheck size={15} aria-hidden="true" /> Secure, encrypted login
          </footer>
        </section>
      </div>

      <footer className="lc__footer">
        <span>Design comparison only · no real sign-in occurs on these routes</span>
        <Link to="/login">Return to the live CPD sign-in</Link>
      </footer>
    </main>
  )
}
